import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Badge } from '@capawesome/capacitor-badge';
import { doc, setDoc, collection, serverTimestamp, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface PushNotificationConfig {
  userId: string;
  onNotificationReceived?: (notification: PushNotificationSchema) => void;
  onNotificationAction?: (action: ActionPerformed) => void;
}

/**
 * Push Notifications via @capacitor/push-notifications.
 * iOS: retorna APNs token (hex 64 chars) — backend manda direto pra APNs com .p8.
 * Android: retorna FCM token — backend manda via FCM normal.
 */
class PushNotificationService {
  private isInitialized = false;
  private currentUserId: string | null = null;

  async initialize(config: PushNotificationConfig): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('ℹ️ Push notifications não disponíveis na web');
      return;
    }

    if (this.isInitialized) {
      this.currentUserId = config.userId;
      return;
    }

    try {
      this.currentUserId = config.userId;

      if (Capacitor.getPlatform() === 'android') {
        await PushNotifications.createChannel({
          id: 'default',
          name: 'Notificações Hostly',
          description: 'Canal padrão de notificações do app',
          importance: 5,
          visibility: 1,
          sound: 'default',
          vibration: true,
        });
      }

      const permissionStatus = await PushNotifications.checkPermissions();
      if (permissionStatus.receive === 'prompt') {
        const result = await PushNotifications.requestPermissions();
        if (result.receive !== 'granted') {
          console.warn('⚠️ Permissão de push negada');
          return;
        }
      } else if (permissionStatus.receive !== 'granted') {
        console.warn('⚠️ Permissão de push não concedida');
        return;
      }

      this.setupListeners(config);
      await PushNotifications.register();

      this.isInitialized = true;
      console.log('✅ Push notifications inicializado');
    } catch (error) {
      console.error('❌ Erro ao inicializar push:', error);
    }
  }

  private setupListeners(config: PushNotificationConfig): void {
    PushNotifications.addListener('registration', async (token: Token) => {
      console.log('✅ Token recebido (' + Capacitor.getPlatform() + '):', token.value.slice(0, 30) + '...');
      if (this.currentUserId) {
        await this.saveToken(this.currentUserId, token.value);
      }
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('❌ Erro registro push:', error);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      console.log('📩 Push recebida (foreground):', notification);
      if (config.onNotificationReceived) config.onNotificationReceived(notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      this.clearBadge();
      if (config.onNotificationAction) config.onNotificationAction(action);
    });
  }

  private async saveToken(userId: string, token: string): Promise<void> {
    try {
      const platform = Capacitor.getPlatform();
      const tokensRef = collection(db, 'users', userId, 'fcmTokens');
      // Garante 1 token por user+platform: apaga tokens antigos da mesma plataforma
      // antes de salvar o novo. Evita push duplicada quando reinstala/rotaciona token.
      const stale = await getDocs(query(tokensRef, where('platform', '==', platform)));
      await Promise.all(
        stale.docs
          .filter((d) => d.id !== token)
          .map((d) => deleteDoc(d.ref))
      );
      const tokenRef = doc(tokensRef, token);
      await setDoc(tokenRef, { token, platform, updatedAt: serverTimestamp(), createdAt: serverTimestamp() }, { merge: true });
      console.log(`✅ Token salvo (${platform}) — ${stale.size - 1 >= 0 ? stale.size : 0} antigos removidos`);
    } catch (error) {
      console.error('❌ Erro ao salvar token:', error);
    }
  }

  async getDeliveredNotifications(): Promise<PushNotificationSchema[]> {
    if (!Capacitor.isNativePlatform()) return [];
    try {
      const result = await PushNotifications.getDeliveredNotifications();
      return result.notifications;
    } catch { return []; }
  }

  async removeAllDeliveredNotifications(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try { await PushNotifications.removeAllDeliveredNotifications(); } catch {}
    await this.clearBadge();
  }

  async clearBadge(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try { await Badge.clear(); } catch {}
  }

  async setBadgeCount(count: number): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      if (count <= 0) await Badge.clear();
      else await Badge.set({ count });
    } catch {}
  }

  async updateUserId(userId: string | null): Promise<void> {
    this.currentUserId = userId;
    if (userId && Capacitor.isNativePlatform() && this.isInitialized) {
      await PushNotifications.register();
    }
  }

  async cleanup(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      await PushNotifications.removeAllListeners();
      this.isInitialized = false;
      this.currentUserId = null;
    } catch {}
  }
}

export const pushNotificationService = new PushNotificationService();
